import type { LoadRow } from '@/data/useLoads';
import { SETUP_STATUSES, type LoadStop, type SetupStatus } from '@/data/types';
import { DOC_STATUSES, type DocStatus } from './fleetDocs';
import { fromDb, type LoadStatus } from './loadStatus';

/**
 * What Dispatch should know about a driver and truck (change spec audit-a-dispatch, design 4.1), pure.
 * `load_dispatch_check` (0301) answers the busy check (C1: another active load of the same driver or truck
 * whose window overlaps) and the documents and broker setup check (C4), plus the two agency policies and
 * which axes they block. The server computes `axes` / `blocked_axes` once; the dialog never recomputes
 * them. A value this portal does not know (a newer token, status or policy) is dropped, never guessed.
 */

export const DISPATCH_POLICIES = ['off', 'warn', 'block'] as const;
export type DispatchPolicy = (typeof DISPATCH_POLICIES)[number];

/** In the server's order. */
export const DISPATCH_AXES = ['driver_busy', 'truck_busy', 'driver_docs', 'truck_docs', 'broker_setup'] as const;
export type DispatchAxis = (typeof DISPATCH_AXES)[number];

/** dispatched..in transit: the loads that make a driver or truck busy (0301, the partial indexes of 0300). */
export const ACTIVE_STATUSES: readonly LoadStatus[] = ['dispatched', 'accepted', 'atPickup', 'loaded', 'inTransit'];

export interface DispatchConflict {
  axis: 'driver' | 'truck';
  status: LoadStatus;
  sameCarrier: boolean;
  /** Null: the load is on a carrier the caller does not run (never named, design D2). */
  loadId: string | null;
  loadNumber: string | null;
  /** Null: that side of the window has no appointment time (open). */
  windowStart: string | null;
  windowEnd: string | null;
}

export interface DocIssue {
  ownerKind: 'driver' | 'truck';
  ownerId: string;
  kind: string;
  status: DocStatus;
  /** Null for a missing document. */
  daysLeft: number | null;
}

export interface DispatchCheck {
  conflictPolicy: DispatchPolicy;
  docsPolicy: DispatchPolicy;
  conflicts: DispatchConflict[];
  docIssues: DocIssue[];
  brokerSetup: { brokerId: string; status: SetupStatus } | null;
  axes: DispatchAxis[];
  blockedAxes: DispatchAxis[];
}

type Raw = Record<string, unknown>;

const isObject = (v: unknown): v is Raw => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const list = (v: unknown): Raw[] => (Array.isArray(v) ? v.filter(isObject) : []);

/** A policy as the server sends it; anything else is the column default, warn. */
export function toPolicy(v: unknown): DispatchPolicy {
  return v === 'off' || v === 'block' ? v : 'warn';
}

function toAxes(v: unknown): DispatchAxis[] {
  return Array.isArray(v) ? v.filter((a): a is DispatchAxis => (DISPATCH_AXES as readonly unknown[]).includes(a)) : [];
}

function toConflict(r: Raw): DispatchConflict | null {
  const axis = r.axis === 'driver' || r.axis === 'truck' ? r.axis : null;
  const status = fromDb(r.status);
  if (!axis || !status) return null;
  return {
    axis,
    status,
    sameCarrier: r.same_carrier === true,
    loadId: str(r.load_id),
    loadNumber: str(r.load_number),
    windowStart: str(r.window_start),
    windowEnd: str(r.window_end),
  };
}

function toDocIssue(r: Raw): DocIssue | null {
  const ownerKind = r.owner_kind === 'driver' || r.owner_kind === 'truck' ? r.owner_kind : null;
  const status = (DOC_STATUSES as readonly unknown[]).includes(r.status) ? (r.status as DocStatus) : null;
  const ownerId = str(r.owner_id);
  const kind = str(r.kind);
  if (!ownerKind || !status || !ownerId || !kind) return null;
  return { ownerKind, ownerId, kind, status, daysLeft: typeof r.days_left === 'number' ? r.days_left : null };
}

function toSetup(v: unknown): DispatchCheck['brokerSetup'] {
  if (!isObject(v)) return null;
  const brokerId = str(v.broker_id);
  const status = (SETUP_STATUSES as readonly unknown[]).includes(v.status) ? (v.status as SetupStatus) : null;
  return brokerId && status ? { brokerId, status } : null;
}

/** The RPC answer -> DispatchCheck; null for no answer (an old server, or nothing to read). */
export function toDispatchCheck(raw: unknown): DispatchCheck | null {
  if (!isObject(raw)) return null;
  return {
    conflictPolicy: toPolicy(raw.dispatch_conflict_policy),
    docsPolicy: toPolicy(raw.require_docs_on_dispatch),
    conflicts: list(raw.conflicts)
      .map(toConflict)
      .filter((c): c is DispatchConflict => c !== null),
    docIssues: list(raw.doc_issues)
      .map(toDocIssue)
      .filter((d): d is DocIssue => d !== null),
    brokerSetup: toSetup(raw.broker_setup),
    axes: toAxes(raw.axes),
    blockedAxes: toAxes(raw.blocked_axes),
  };
}

/** Some finding the agency's rules only warn about: the dialog asks for the tick before Dispatch anyway. */
export function needsConfirm(c: DispatchCheck | null): boolean {
  return !!c && c.axes.some((a) => !c.blockedAxes.includes(a));
}

/** Some finding the agency's rules block: Dispatch is disabled (the server refuses it too). */
export function isBlocked(c: DispatchCheck | null): boolean {
  return !!c && c.blockedAxes.length > 0;
}

export function conflictsOf(c: DispatchCheck | null, axis: 'driver' | 'truck'): DispatchConflict[] {
  return c ? c.conflicts.filter((x) => x.axis === axis) : [];
}

export function docIssuesOf(c: DispatchCheck | null, ownerKind: 'driver' | 'truck'): DocIssue[] {
  return c ? c.docIssues.filter((d) => d.ownerKind === ownerKind) : [];
}

const pickupAt = (r: LoadRow) => r.pickupStop?.appointmentAt ?? null;

/**
 * The load this driver or truck is on right now, from the in-flight list the office already reads: the
 * earliest (by first pickup appointment, none last, then oldest) dispatched..in-transit row that is not
 * `exclude` (the load being dispatched). Only rows the caller may read: another carrier's load is not here.
 */
export function activeLoadOf(rows: readonly LoadRow[], by: { driverId?: string | null; truckId?: string | null }, exclude: string): LoadRow | null {
  const { driverId, truckId } = by;
  if (!driverId && !truckId) return null;
  const hits = rows.filter(
    (r) =>
      r.id !== exclude &&
      r.status !== 'unknown' &&
      ACTIVE_STATUSES.includes(r.status) &&
      ((!!driverId && r.driverId === driverId) || (!!truckId && r.truckId === truckId)),
  );
  hits.sort((a, b) => {
    const pa = pickupAt(a);
    const pb = pickupAt(b);
    if (pa !== pb) {
      if (pa === null) return 1;
      if (pb === null) return -1;
      return Date.parse(pa) - Date.parse(pb);
    }
    return a.createdAt.localeCompare(b.createdAt);
  });
  return hits[0] ?? null;
}

/** Where that load goes next: its pickup until the driver has left it, then its last delivery. */
export function nextStopOf(row: LoadRow): LoadStop | null {
  if (row.pickupStop && !row.pickupStop.departedAt) return row.pickupStop;
  return row.deliveryStop ?? null;
}
